import { useState, useEffect, useRef, Suspense } from 'react';
import { Outlet, useLocation, useSearchParams } from 'react-router-dom';
import { useToastStore } from '@/store/toast.store';
import Sidebar from './Sidebar';
import { useAuthStore } from '@/store/auth.store';
import { api } from '@/services/api';
import { useBrandingStore } from '@/store/branding.store';
import { useBrandingInit } from '@/hooks/useBrandingInit';
import { NotificationBell, NotificationPanel } from '../notifications';
import { useNotifications } from '@/hooks/useNotifications';
import { MessagingToggle, MessagingPanel } from '../messaging';
import { MessagingSocketProvider } from '../messaging/MessagingSocketProvider';
import { useIncidentThreadAutoJoin } from '@/hooks/useIncidentThreadAutoJoin';
import { ScheduledMessagesProcessor } from '../messaging/ScheduledMessagesProcessor';
import RouteAnnouncer from './RouteAnnouncer';
import AiAssistantPanel from '../ai-assistant/AiAssistantPanel';
import { useAiAssistantStore } from '@/store/ai-assistant.store';
import CommandPalette from '../common/CommandPalette';
import KeyboardShortcutsModal from '../common/KeyboardShortcutsModal';
import { useGlobalKeyboardShortcuts } from '@/hooks/useGlobalKeyboardShortcuts';
import { useKeyboardDismiss } from '@/hooks/useKeyboardDismiss';
import { useIdleTimeout } from '@/hooks/useIdleTimeout';
import { useIsDesktop } from '@/hooks/useMediaQuery';
import { useOfflineCache } from '@/hooks/useOfflineCache';
import { useTenantStore } from '@/store/tenant.store';
import { useIncidentStore } from '@/store/incident.store';
import FaIcon from '@/components/common/FaIcon';
import { IconButton } from '@/components/common/Button';
import { MenuIcon } from '../icons';
import MobileBottomNav from './MobileBottomNav';
import DesktopTopBar from './DesktopTopBar';
import PageTransition from '@/components/common/PageTransition';
import OfflineIndicator from '@/components/common/OfflineIndicator';
import Breadcrumb from '@/components/common/Breadcrumb';
import LoadingState from '@/components/common/LoadingState';
import TrialBanner from './TrialBanner';
import ExerciseBanner from './ExerciseBanner';
import { warmArcgisSdkOnIdle } from '@/utils/arcgis-warmup';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import ErrorState from '@/components/common/ErrorState';
import SosAlarmListener from '@/components/safety/SosAlarmListener';
import { useEmbeddedMode } from '@/hooks/useEmbeddedMode';
import { getShellChrome } from '@/utils/embedded-console';

export default function MainLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const { logoUrl, appName } = useBrandingStore();
  const { togglePanel: toggleAiPanel } = useAiAssistantStore();
  // Only render one of {mobile header, desktop top bar} at a time. Using
  // pure responsive `hidden lg:flex` / `lg:hidden` classes left both copies
  // in the DOM, so screen readers announced duplicate buttons (HEADER-DUP-001).
  const isDesktop = useIsDesktop();
  // Embedded in the native app (M2-CONSOLE): the native shell already shows
  // the product, its own navigation and device status, so the console keeps
  // only its compact header + drawer at every width (utils/embedded-console).
  const embedded = useEmbeddedMode();
  const chrome = getShellChrome({ embedded, isDesktop });

  // Initialize branding on tenant change
  useBrandingInit();

  // Trial activation lands on /?welcome=trial — greet once and strip the param
  const [searchParams, setSearchParams] = useSearchParams();
  const toastSuccess = useToastStore((s) => s.success);
  useEffect(() => {
    if (searchParams.get('welcome') !== 'trial') return;
    toastSuccess('Welcome to Command Bridge! Your 90-day trial is active.');
    const next = new URLSearchParams(searchParams);
    next.delete('welcome');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Initialize notifications (handles polling)
  useNotifications();

  // Auto-join incident threads when incident is activated
  useIncidentThreadAutoJoin();

  // Global keyboard shortcuts (navigation chords, AI toggle, search focus, etc.)
  useGlobalKeyboardShortcuts({ onEscape: () => setSidebarOpen(false) });

  // Dismiss keyboard on scroll (mobile)
  useKeyboardDismiss();

  // Log out after tenant-configured idle timeout
  useIdleTimeout();

  // Pre-warm offline caches (tasks, shelters, board, contacts, credentials,
  // members, supply requests, map features) on app mount + tenant switch.
  // Contacts and credentials are console-only: field devices skip them (P2-05).
  // Without this, IndexedDB stores remain empty until the user visits a Field
  // page that triggers a per-module sync — see OFFLINE-001.
  const activeTenantId = useTenantStore(s => s.activeTenant?.id ?? null);
  const activeIncidentId = useIncidentStore(s => s.activeIncident?.id ?? null);
  useOfflineCache(activeTenantId, activeIncidentId);

  // Warm the ArcGIS SDK chunk (~3.9 MB gzip, excluded from precache) and the
  // default basemap's style/tiles in the background while the user is on
  // non-map pages, so the first Map visit paints tiles almost immediately.
  // Idle-scheduled and skipped on constrained (saveData/2g) links.
  useEffect(() => {
    warmArcgisSdkOnIdle();
  }, []);

  // Refresh user profile on mount (and on re-login) to keep pre-signed avatar URLs current
  const currentUserId = useAuthStore(s => s.user?.id);
  useEffect(() => {
    const auth = useAuthStore.getState();
    if (!auth.isAuthenticated || !auth.token) return;
    api.get<{ data: { avatar_url?: string; name?: string; phone?: string; job_title?: string } }>('/auth/me')
      .then(res => {
        const current = useAuthStore.getState().user;
        if (current && res.data) {
          useAuthStore.getState().setUser({
            ...current,
            avatar_url: res.data.avatar_url,
            name: res.data.name || current.name,
            phone: res.data.phone || undefined,
            job_title: res.data.job_title || undefined,
          });
        }
      })
      .catch(() => {});
  }, [currentUserId]);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when sidebar is open on mobile
  useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [sidebarOpen]);

  // Swipe-to-dismiss for mobile sidebar
  const touchStartX = useRef<number>(0);
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    const deltaX = e.changedTouches[0].clientX - touchStartX.current;
    if (deltaX < -80) {
      setSidebarOpen(false);
    }
  };

  return (
    <MessagingSocketProvider>
    <RouteAnnouncer />
    <ScheduledMessagesProcessor />
    <div className="flex h-screen bg-surface-secondary overflow-hidden print:block print:h-auto print:overflow-visible">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:z-[100] focus:top-2 focus:left-2 focus:bg-primary-600 focus:text-white focus:px-4 focus:py-2 focus:rounded-md focus:text-sm">
        Skip to main content
      </a>
      <div className="print:hidden"><OfflineIndicator /></div>
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className={`fixed inset-0 z-[60] bg-gray-900/80 backdrop-blur-sm ${chrome.desktopSidebar ? 'lg:hidden' : ''}`}
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Mobile sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-[70] w-72 max-w-[85vw] transform transition-transform duration-300 ease-in-out ${chrome.desktopSidebar ? 'lg:hidden' : ''} print:hidden ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}
        role="dialog"
        aria-modal="true"
        aria-label="Sidebar navigation"
        // The closed drawer is only slid off-screen (-translate-x-full); it stays
        // `display: block; visibility: visible` so it can animate. That left 83 focusable
        // controls in the tab order at mobile widths — a keyboard or switch-control user
        // tabbing the page walked into an invisible nav — and `aria-modal="true"` on a
        // CLOSED dialog tells assistive tech the rest of the page is inert when it is not.
        // Mirrors the inert already applied to the main content when the drawer is open
        // (below), so exactly one of the two is interactive at any time.
        aria-hidden={!sidebarOpen}
        {...(!sidebarOpen ? { inert: '' as unknown as undefined } : {})}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div className="relative flex h-full flex-col">
          {/* Close button */}
          <IconButton
            icon={<FaIcon icon="xmark" className="h-6 w-6" />}
            label="Close sidebar"
            variant="ghost"
            size="sm"
            onClick={() => setSidebarOpen(false)}
            className="absolute right-4 top-4 z-10 text-gray-400 hover:bg-gray-800 hover:text-white"
          />
          <Sidebar onNavigate={() => setSidebarOpen(false)} />
        </div>
      </div>

      {/* Desktop sidebar (not embedded: the native shell has its own navigation) */}
      {chrome.desktopSidebar && (
        <div className="hidden lg:flex lg:w-64 lg:flex-col lg:fixed lg:inset-y-0 print:hidden">
          <Sidebar />
        </div>
      )}

      {/* Main content */}
      {/* When the mobile drawer is open, mark main as inert (better than aria-hidden:
          inert blocks focus from descendants, satisfying WAI-ARIA + WCAG without
          the "aria-hidden on focused element" browser warning). */}
      <div className={`flex flex-1 flex-col min-w-0 ${chrome.sidebarOffset ? 'lg:pl-64' : ''}`} {...(sidebarOpen ? { inert: '' as unknown as undefined } : {})}>
        {/* Only one header variant is in the DOM at a time so screen readers
            don't announce duplicate Notification/AI Assistant buttons. */}
        {chrome.compactHeader && (
          <div
            className="sticky top-0 z-30 flex items-center gap-x-3 border-b border-gray-200 bg-white px-4 shadow-sm dark:border-[#0d1520] dark:bg-[#0d1520]"
            style={{ paddingTop: 'max(0.5rem, env(safe-area-inset-top))', paddingBottom: '0.5rem' }}
          >
            <div className="flex-1 flex items-center gap-2 min-w-0">
              {chrome.brand && (<>
              <img
                className="h-8 w-auto flex-shrink-0"
                src={logoUrl || '/lightShield.png'}
                alt={appName}
                onError={(e) => {
                  // Tenant logo failed (404/429/CORS) — fall back to the default
                  // Command Bridge shield so the mobile header doesn't show a
                  // broken image icon.
                  const img = e.currentTarget;
                  if (img.src.indexOf('/lightShield.png') === -1) {
                    img.src = '/lightShield.png';
                  }
                }}
              />
              <span className="font-semibold text-theme-primary truncate">{appName}</span>
              </>)}
            </div>
            {/* AI Assistant Toggle */}
            <IconButton
              icon={<FaIcon icon="sparkles" className="h-6 w-6" />}
              label="Open AI Assistant"
              variant="ghost"
              size="sm"
              onClick={toggleAiPanel}
              className="text-theme-secondary hover:text-purple-600 dark:hover:text-purple-400"
            />
            {/* Messaging Toggle */}
            <MessagingToggle />
            {/* Notification Bell */}
            <div className="relative">
              <NotificationBell className="text-theme-secondary" />
              <NotificationPanel />
            </div>
            {/* Menu Button */}
            <IconButton
              icon={<MenuIcon className="h-6 w-6" />}
              label="Open sidebar menu"
              variant="ghost"
              size="sm"
              onClick={() => setSidebarOpen(true)}
              className="text-theme-secondary"
            />
          </div>
        )}

        {/* Desktop top bar — also gated so its bell/AI buttons aren't
            duplicated in the DOM alongside the mobile header. */}
        <div className="print:hidden">{chrome.desktopTopBar && <DesktopTopBar />}</div>

        {/* Trial-status bar (renders nothing for non-trial tenants) */}
        <div className="print:hidden"><TrialBanner /></div>

        {/* EXERCISE safety banner — shown only when an exercise incident is engaged */}
        <div className="print:hidden"><ExerciseBanner /></div>

        {/* Breadcrumb navigation (desktop only, hidden on map/field routes) */}
        {chrome.breadcrumb && <div className="print:hidden"><Breadcrumb /></div>}

        {/* Page content — map and field routes need no bottom padding (content fills edge-to-edge) */}
        {/* `relative` makes <main> the containing block for absolutely-positioned
            descendants (sr-only spans, dropdowns). Without it their overflow
            escapes to the document and creates a phantom page-level scrollbar
            underneath the app shell. */}
        {/* tabIndex={-1}: without it the skip link scrolls the viewport but does
            not move focus, so the next Tab resumes inside the sidebar the user
            was trying to bypass -- the link looks like it works and does not. */}
        <main
          id="main-content"
          tabIndex={-1}
          className={`relative flex-1 overflow-y-auto overflow-x-hidden print:overflow-visible print:h-auto ${
            !chrome.bottomNav || location.pathname === '/map' || location.pathname.startsWith('/field')
              ? ''
              : 'main-mobile-padding'
          }`}
        >
          <Suspense fallback={<LoadingState className="h-full" message="Loading…" />}>
            {/*
              A page that throws must not take the console with it.

              The only app-wide boundary sits above this layout, so any render
              error in any of ~280 routed pages replaced the ENTIRE screen with a
              full-page "Something went wrong" — sidebar, incident switcher,
              status bar and all — whose only offers are a reload or a
              clear-everything-and-reload. During an incident that costs the
              operator their whole console, and their engaged incident context,
              because one page had a bad render.

              Scoping the boundary to the routed content keeps the shell alive:
              the failure reads as one broken page inside a working console, and
              the operator can navigate away from it.

              `resetKeys` on the pathname is what makes navigating away actually
              work — a boundary latches once it catches, so without this they
              would carry the dead page's error onto every route they visited
              next.
            */}
            <ErrorBoundary
              resetKeys={[location.pathname]}
              fallback={
                <ErrorState
                  headingLevel={2}
                  title="This page didn't load"
                  message="Something went wrong rendering this page. The rest of the console is unaffected — try again, or pick another page from the menu."
                  onRetry={() => window.location.reload()}
                />
              }
            >
              <PageTransition>
                <Outlet />
              </PageTransition>
            </ErrorBoundary>
          </Suspense>
        </main>
      </div>

      {/* Mobile bottom navigation */}
      {chrome.bottomNav && <div className="print:hidden"><MobileBottomNav onMenuPress={() => setSidebarOpen(true)} /></div>}

      {/* Toast Notifications (bottom-left) */}

      {/* Messaging Panel */}
      <MessagingPanel />

      {/* AI Assistant Panel */}
      <AiAssistantPanel />

      {/* Command Palette */}
      <CommandPalette />

      {/* Keyboard Shortcuts Modal */}
      <KeyboardShortcutsModal />

      {/* Responder SOS alarm — persistent modal + audible alarm on every
          console page, with a polling fallback when the socket is down. */}
      <SosAlarmListener />
    </div>
    </MessagingSocketProvider>
  );
}
